/**
 * The account client: the /api surface the server exposes, and the login token.
 *
 * Since accounts arrived, THE DATABASE IS THE PROFILE. The client no longer
 * persists a profile of its own anywhere — localStorage holds exactly one
 * account-related thing, the login token, and everything else (inventory,
 * credits, XP, loadouts, pass, challenges) is fetched from the server and
 * mutated only by asking it. See server/accounts.ts for the other half.
 *
 * Every call returns a discriminated result rather than throwing: a fetch
 * failure and a refusal are both ordinary outcomes the UI has a sentence for.
 *
 * DOM-safe: `fetch` is injected (defaulting lazily to the global), and token
 * storage rides the same `browserStorage()` profile.ts guards with.
 */

import type { PlayerProfile } from './cosmetics';
import { legacyKeyFor, readMigrating } from './storage-migration';
import { browserStorage, sanitizeProfile, type ProfileStorage } from './profile';
import { sanitizePassClaims, type PassClaims } from './rocketpass';
import { normalizeServerUrl } from '../net/ws-transport';
import { STARTING_RATING, type MatchOutcome, type RatingRecord } from './rating';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AccountUser {
  id: number;
  email: string;
  displayName: string;
  emailVerified: boolean;
}

/** What login and /api/me answer with. */
export interface AccountSession {
  token: string;
  user: AccountUser;
  profile: PlayerProfile;
  claims: PassClaims;
}

/** A profile mutation's answer: a toast line and the authoritative state. */
export interface ProfileUpdate {
  message: string;
  profile: PlayerProfile;
  claims: PassClaims;
}

export type ApiResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: string; status: number };

/** An operation the server applies to the profile. Mirrors server/api.ts. */
export type ProfileOp =
  | { kind: 'buy'; itemId: string; paint?: string }
  | { kind: 'claim-tier'; tier: number; track: 'free' | 'premium' }
  | { kind: 'claim-all-tiers' }
  | { kind: 'unlock-premium' }
  | { kind: 'claim-challenge'; id: string }
  | { kind: 'claim-all-challenges' }
  | { kind: 'loadout'; presets: unknown; activePresetId: string }
  | { kind: 'match'; stats: unknown; won: boolean; mode?: string };

// ---------------------------------------------------------------------------
// Token storage — the ONE account thing kept client-side
// ---------------------------------------------------------------------------

export const AUTH_STORAGE_KEY = 'vroomball.auth';

export const loadAuthToken = (storage: ProfileStorage | null = browserStorage()): string | null => {
  if (storage === null) return null;
  try {
    const raw = readMigrating(storage, AUTH_STORAGE_KEY, legacyKeyFor(AUTH_STORAGE_KEY));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    const token = (parsed as { token?: unknown })?.token;
    return typeof token === 'string' && token.length > 0 && token.length <= 128 ? token : null;
  } catch {
    return null;
  }
};

export const saveAuthToken = (
  token: string | null,
  storage: ProfileStorage | null = browserStorage()
): void => {
  if (storage === null) return;
  try {
    if (token === null) storage.removeItem(AUTH_STORAGE_KEY);
    else storage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ version: 1, token }));
  } catch {
    // Storage full or disabled. The session still works; it just will not
    // survive a reload, which is the honest degradation.
  }
};

// ---------------------------------------------------------------------------
// Address derivation
// ---------------------------------------------------------------------------

/**
 * The HTTP base for a stored game-server address: the same host and port the
 * WebSocket dials, with the scheme swapped. One address setting covers both.
 */
export const apiBaseFor = (serverAddress: string, secure = false): string | null => {
  const url = normalizeServerUrl(serverAddress, secure);
  if (url === null) return null;
  try {
    const parsed = new URL(url);
    const scheme = parsed.protocol === 'wss:' ? 'https:' : 'http:';
    return `${scheme}//${parsed.host}`;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// The API client
// ---------------------------------------------------------------------------

type FetchLike = (input: string, init?: {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
}) => Promise<{ status: number; json(): Promise<unknown> }>;

const OFFLINE = 'Could not reach the server. Check the address and that it is running.';

export interface AccountApiOptions {
  /** Where the server lives right now. Read per call, so the address row works. */
  base: () => string | null;
  /** Injected for tests; defaults lazily to the global fetch. */
  fetchFn?: FetchLike;
}

export class AccountApi {
  private readonly base: () => string | null;
  private readonly fetchFn: FetchLike;

  constructor(options: AccountApiOptions) {
    this.base = options.base;
    this.fetchFn = options.fetchFn ?? ((input, init) => fetch(input, init));
  }

  register(email: string, displayName: string, password: string): Promise<ApiResult<{ message: string }>> {
    return this.call('POST', '/api/register', null, { email, displayName, password }, raw => ({
      message: asMessage(raw),
    }));
  }

  resend(email: string, password: string): Promise<ApiResult<{ message: string }>> {
    return this.call('POST', '/api/resend', null, { email, password }, raw => ({
      message: asMessage(raw),
    }));
  }

  /** Ask for a password-reset mail. The link opens a page the server renders. */
  resetRequest(email: string): Promise<ApiResult<{ message: string }>> {
    return this.call('POST', '/api/reset-request', null, { email }, raw => ({
      message: asMessage(raw),
    }));
  }

  login(email: string, password: string): Promise<ApiResult<AccountSession>> {
    return this.call('POST', '/api/login', null, { email, password }, parseSession);
  }

  me(token: string): Promise<ApiResult<AccountSession>> {
    return this.call('GET', '/api/me', token, null, parseSession);
  }

  logout(token: string): Promise<ApiResult<{ message: string }>> {
    return this.call('POST', '/api/logout', token, {}, raw => ({ message: asMessage(raw) }));
  }

  /**
   * Everything the ranked screen shows, in one round trip per part.
   *
   * Three calls rather than one aggregate endpoint, because the three have
   * different audiences: the standing and the history are the signed-in
   * player's, and the leaderboard is public and identical for everybody. An
   * endpoint that returned all three would have to be authenticated, which
   * would make the one page worth linking to unlinkable.
   */
  ranked(token: string): Promise<ApiResult<{ ratings: RatingRecord[] }>> {
    return this.call('GET', '/api/ranked/me', token, null, raw => ({
      ratings: sanitizeRatings((raw as Record<string, unknown>).ratings),
    }));
  }

  rankedHistory(token: string, limit = 10): Promise<ApiResult<{ matches: RankedMatch[] }>> {
    return this.call('GET', `/api/ranked/history?limit=${limit}`, token, null, raw => ({
      matches: sanitizeHistory((raw as Record<string, unknown>).matches),
    }));
  }

  leaderboard(mode: string, limit = 20): Promise<ApiResult<{ entries: LeaderboardEntry[] }>> {
    return this.call(
      'GET',
      `/api/ranked/leaderboard?mode=${encodeURIComponent(mode)}&limit=${limit}`,
      null,
      null,
      raw => ({ entries: sanitizeLeaderboard((raw as Record<string, unknown>).entries) })
    );
  }

  /** Ask the server to apply one profile mutation and answer with the truth. */
  apply(token: string, op: ProfileOp): Promise<ApiResult<ProfileUpdate>> {
    const [path, body] = opRequest(op);
    return this.call('POST', path, token, body, raw => {
      const record = raw as Record<string, unknown>;
      return {
        message: asMessage(raw),
        profile: sanitizeProfile(record.profile),
        claims: sanitizePassClaims({ version: 1, ...(record.claims as object ?? {}) }),
      };
    });
  }

  private async call<T>(
    method: string,
    path: string,
    token: string | null,
    body: unknown,
    parse: (raw: unknown) => T
  ): Promise<ApiResult<T>> {
    const base = this.base();
    if (base === null) {
      return { ok: false, error: 'That server address is not usable. Set one on the Multiplayer screen.', status: 0 };
    }
    try {
      const response = await this.fetchFn(`${base}${path}`, {
        method,
        headers: {
          ...(body === null ? {} : { 'Content-Type': 'application/json' }),
          ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
        },
        ...(body === null ? {} : { body: JSON.stringify(body) }),
      });
      const raw: unknown = await response.json().catch(() => ({}));
      if (response.status >= 200 && response.status < 300) {
        return { ok: true, value: parse(raw) };
      }
      const error = (raw as { error?: unknown })?.error;
      return {
        ok: false,
        error: typeof error === 'string' && error.length > 0 ? error : `The server answered ${response.status}.`,
        status: response.status,
      };
    } catch {
      return { ok: false, error: OFFLINE, status: 0 };
    }
  }
}

// ---------------------------------------------------------------------------
// Parsing — nothing off a wire is trusted, including our own server's answers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Ranked payloads
// ---------------------------------------------------------------------------

/** One row of a leaderboard page. Mirrors `LeaderboardRow` on the server. */
export interface LeaderboardEntry {
  rank: number;
  userId: number;
  displayName: string;
  rating: number;
  played: number;
  wins: number;
  losses: number;
  draws: number;
}

/** One line of the player's own history. Mirrors `MatchHistoryRow`. */
export interface RankedMatch {
  at: number;
  mode: string;
  team: 0 | 1;
  scoreFor: number;
  scoreAgainst: number;
  outcome: MatchOutcome;
  ranked: boolean;
  goals: number;
  ratingBefore: number;
  ratingAfter: number;
}

/**
 * Sanitizers, not parsers, and for the same reason every stored payload in
 * this tree has one: what comes back over the wire is whatever the other end
 * sent. A NaN rating that reaches the tier table renders as an empty chip and
 * the bug is three screens away from where it came from.
 */
const num = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const str = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const asOutcome = (value: unknown): MatchOutcome =>
  value === 'win' || value === 'loss' || value === 'draw' ? value : 'draw';

const sanitizeRatings = (value: unknown): RatingRecord[] =>
  asArray(value).map(entry => {
    const row = entry as Record<string, unknown>;
    return {
      mode: str(row.mode, 'duel'),
      rating: num(row.rating, STARTING_RATING),
      played: num(row.played),
      wins: num(row.wins),
      losses: num(row.losses),
      draws: num(row.draws),
    };
  });

const sanitizeLeaderboard = (value: unknown): LeaderboardEntry[] =>
  asArray(value).map((entry, index) => {
    const row = entry as Record<string, unknown>;
    return {
      rank: num(row.rank, index + 1),
      userId: num(row.userId),
      displayName: str(row.displayName, 'Player'),
      rating: num(row.rating, STARTING_RATING),
      played: num(row.played),
      wins: num(row.wins),
      losses: num(row.losses),
      draws: num(row.draws),
    };
  });

const sanitizeHistory = (value: unknown): RankedMatch[] =>
  asArray(value).map(entry => {
    const row = entry as Record<string, unknown>;
    return {
      at: num(row.at),
      mode: str(row.mode, 'duel'),
      team: row.team === 1 ? 1 : 0,
      scoreFor: num(row.scoreFor),
      scoreAgainst: num(row.scoreAgainst),
      outcome: asOutcome(row.outcome),
      ranked: row.ranked === true,
      goals: num(row.goals),
      ratingBefore: num(row.ratingBefore, STARTING_RATING),
      ratingAfter: num(row.ratingAfter, STARTING_RATING),
    };
  });

const asMessage = (raw: unknown): string => {
  const message = (raw as { message?: unknown })?.message;
  return typeof message === 'string' ? message : '';
};

const parseSession = (raw: unknown): AccountSession => {
  const record = raw as Record<string, unknown>;
  const user = (record.user ?? {}) as Record<string, unknown>;
  return {
    token: typeof record.token === 'string' ? record.token : '',
    user: {
      id: typeof user.id === 'number' ? user.id : 0,
      email: typeof user.email === 'string' ? user.email : '',
      displayName: typeof user.displayName === 'string' ? user.displayName : 'Player',
      emailVerified: user.emailVerified === true,
    },
    profile: sanitizeProfile(record.profile),
    claims: sanitizePassClaims({ version: 1, ...((record.claims as object) ?? {}) }),
  };
};

const opRequest = (op: ProfileOp): [string, Record<string, unknown>] => {
  switch (op.kind) {
    case 'buy':
      return ['/api/profile/buy', { itemId: op.itemId, ...(op.paint === undefined ? {} : { paint: op.paint }) }];
    case 'claim-tier':
      return ['/api/profile/claim-tier', { tier: op.tier, track: op.track }];
    case 'claim-all-tiers':
      return ['/api/profile/claim-all-tiers', {}];
    case 'unlock-premium':
      return ['/api/profile/unlock-premium', {}];
    case 'claim-challenge':
      return ['/api/profile/claim-challenge', { id: op.id }];
    case 'claim-all-challenges':
      return ['/api/profile/claim-all-challenges', {}];
    case 'loadout':
      return ['/api/profile/loadout', { presets: op.presets, activePresetId: op.activePresetId }];
    case 'match':
      return ['/api/profile/match', { stats: op.stats, won: op.won, ...(op.mode === undefined ? {} : { mode: op.mode }) }];
  }
};
